import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AmplifyCore,
  commonActions,
  xcaliberActions
} from '@codametrix/ui-common';
import history from '../history';
import { retrieveGroups } from '../core/navigation';
import { getContextHomepage } from '../core/preferences/preferences';
import { NEW_DESIGN_SUFFIX } from '../core/utils/newDesign';
import { isNewDesign } from '../core/middleware/new-design-middleware';
import { saveFormData } from './account-settings-state.actions';
import { applyDefaultFilters, clearAllFilters } from './cases.action';
import { navigate, setBackToWorklistPath } from './ui-core.actions';
export interface NavigateContextResult {
  item: {
    id: string;
    label?: string;
    observationService?: string;
    resourceId?: string;
    parentId?: string;
    [key: string]: any;
  };
  path: string;
  isDefault?: boolean;
}

const { UserInterface } = commonActions;

const UNDEFINED_PREF_CHOICE = undefined;

const hasEnabled = (option: CMxAPI.NavAction) => option.enabled;

const firstGroup = (context: CMx.Context) => {
  if (context === null || context.activeContext === null) {
    return false;
  }

  const groups = retrieveGroups(context.activeContext, '*');
  const firstGroup = groups.find(
    group => group.key !== 'search' && group.options.some(hasEnabled)
  );
  if (!firstGroup) {
    return false;
  }
  return firstGroup.options.find(hasEnabled);
};

/* The "context" within the navFields object doesn't seem to contain serviceLineName. But if
   we manually get the state and look for the uiContext it does have the serviceLineName.
   Below was reworked to get CDMX-2845 across the finish line and should be investigated further
   and documented. */
const navigateContext = createAsyncThunk<
  NavigateContextResult | null,
  CMx.NavData,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  UserInterface.NAVIGATE_CONTEXT,
  async (navFields, { dispatch, getState }) => {
    const { context, homepagePreference } = navFields;

    const state = getState() as any;
    const uiContext: CMx.Context = state.ui?.context ?? context;
    const serviceLineName = state.ui?.context?.serviceLineName ?? '';

    const useNewDesignSuffix = isNewDesign;
    let defaultHomepage;
    if (uiContext.activeContext) {
      defaultHomepage = getContextHomepage(
        uiContext.activeContext?.tenantId,
        homepagePreference
      );
    }

    const hasProviderDirectory = context.activeContext
      ? AmplifyCore.benchEditable.isResourceAllowed(
          uiContext.activeContext,
          'PROVIDER_DIRECTORY',
          serviceLineName
        )
      : false;

    if (hasProviderDirectory) {
      xcaliberActions.loadXCaliber(dispatch as any);
    }

    dispatch(clearAllFilters() as any);
    dispatch(applyDefaultFilters(uiContext) as any);
    const option = firstGroup(uiContext);
    if (!option) {
      history.replace(`/cmx/`);
      return { item: { id: 'root_fallback', label: 'CMX Root' }, path: '' };
    } 
    if (defaultHomepage) {
      dispatch(
        saveFormData({
          defaultOrg: UNDEFINED_PREF_CHOICE,
          defaultServiceLine: UNDEFINED_PREF_CHOICE,
          defaultHomepage
        })
      );
      const resolvedPath = `${defaultHomepage.path}${isNewDesign ? NEW_DESIGN_SUFFIX : ''}`;
      history.replace(`/cmx/${resolvedPath}`);
      return { 
        item: { id: defaultHomepage.id || 'default_homepage', label: defaultHomepage.name, ...defaultHomepage, observationService: defaultHomepage.observationService || 'unknown' },
        path: resolvedPath,
        isDefault: true
      };
    } 
    dispatch(navigate(option));
    dispatch(setBackToWorklistPath(option));
    const resolvedPath = `${option.path}${isNewDesign ? NEW_DESIGN_SUFFIX : ''}`;
    history.replace(`/cmx/${resolvedPath}`);
    return { 
      item: { id: option.id || option.key || 'unknown_option', label: option.label, ...option, observationService: option.observationService || 'unknown' },
      path: resolvedPath 
    };
  }
);

const navigateAndReload = (context: CMx.Context, dispatch: any) => {
  const option = firstGroup(context);
  if (option) {
    globalThis.location.replace(`/cmx/${option.path}`);
  }
};

export { firstGroup, navigateAndReload, navigateContext };
